import { useGlobalStore, TypeNotification } from '@/store/globalStore';
import KeycloakService from '@/services/keycloakService';
import axios, { ResponseType } from 'axios';

export enum HTTPMethods {
  CONNECT = 'CONNECT',
  DELETE = 'DELETE',
  GET = 'GET',
  HEAD = 'HEAD',
  OPTIONS = 'OPTIONS',
  PATCH = 'PATCH',
  POST = 'POST',
  PUT = 'PUT',
  TRACE = 'TRACE',
}

export type CallApiParamsType = {
  method?: HTTPMethods; // = HTTPMethods.GET;
  path: string;
  body?: object | null; // = null;
  responseType?: ResponseType | undefined;
  options?: object; // = {};
  globalLoader?: boolean; // = true;
  dataFormater?: ((data: any) => void) | null; // = null;

  successCallback?: ((data: any) => void) | null; // = null;
  successType?: TypeNotification;
  successMessage?: string | null;

  errorCallback?: ((error: any) => void) | null; // = null;
  errorType?: TypeNotification; // = TypeNotification.ERROR;
  errorMessage?: string; // = 'Erreur API';
};

/**
 * @description Use callApi to fetch API and deal with response or error
 * @param {method} - Method to call api with of type HTTPMethods - default is get
 * @param {body} - Body to send to the api - default is null
 * @param {options} - options sended with the request
 *  - Default: headers: { 'Content-type': 'application/json; charset=UTF-8' },
 * @param {loader} - Activate or innactivate the loader,
 * @param {dataFormater} - function to format result, will execute on every success if is not null - default is null
 * @param {successCallback} - callback function executed when api call is success - default is null
 * @param {successType} - type of alert - default is success - (Alert will only execute if successMessage is not null)
 * @param {successMessage} - message to display in alert - default is null
 * @param {errorCallback} - callback function executed when api call is error - default is null
 * @param {errorType} - type of alert - default is error
 * @param {errorMessage} - message to display in alert - default is 'Erreur API'
 */

export const callApi = async ({
  method = HTTPMethods.GET,
  path,
  options = method === HTTPMethods.GET
    ? {}
    : {
        headers: {
          'Content-type': 'application/json; charset=UTF-8',
        },
      },
  body = null,
  responseType = undefined,
  globalLoader = true,
  dataFormater = null,
  successCallback = null,
  successType = TypeNotification.SUCCESS,
  successMessage = null,
  errorCallback = null,
  errorType = TypeNotification.ERROR,
  errorMessage = '',
}: CallApiParamsType) => {
  const globalStore = useGlobalStore();
  try {
    let response;
    if (globalLoader) {
      globalStore.addGlobalLoader();
    }
    const authHeaders = {
      Authorization: `Bearer ${KeycloakService.token()}`,
      ...(options || {}),
    };
    // the solution of : if i send request with get method i can't send it with token
    if (method === HTTPMethods.GET && !options) {
      response = await axios.get(path, {
        headers: { Authorization: `Bearer ${KeycloakService.token()}` },
        ...(responseType && { responseType: responseType }),
      });
    } else if (method == HTTPMethods.DELETE) {
      response = await axios({
        method,
        url: path,
        headers: authHeaders,
        responseType,
      });
    } else {
      response = await axios({
        method,
        url: path,
        data: body,
        headers: authHeaders,
        responseType,
      });
    }

    if (!response) {
      if (globalLoader) {
        globalStore.removeOneGlobalLoader();
      }
      if (errorCallback) {
        await errorCallback(null);
      } else if (errorMessage) {
        globalStore.showNotification(errorType, errorMessage);
      }
    } else {
      if (successCallback) {
        if (!dataFormater) {
          await successCallback(response?.data);
        } else {
          await successCallback(dataFormater(response?.data));
        }
      } else {
        if (!dataFormater) {
          return response?.data;
        } else {
          return dataFormater(response?.data);
        }
      }
      if (successMessage) {
        globalStore.showNotification(successType, successMessage);
      }
      if (globalLoader) {
        globalStore.removeOneGlobalLoader();
      }
    }
  } catch (error: any) {
    if (globalLoader) {
      globalStore.removeOneGlobalLoader();
    }
    // Extraire le message d'erreur de la réponse API si disponible
    const apiErrorMessage = error?.response?.data?.message;
    const finalErrorMessage = apiErrorMessage || errorMessage;

    if (errorCallback) {
      await errorCallback(error);
    } else {
      if (finalErrorMessage && errorMessage) {
        globalStore.showNotification(errorType, finalErrorMessage);
      }
    }
  }
};
